import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import { PublicGuard } from "@/components/auth-guard";
import { AuthShell } from "@/components/auth-shell";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heading } from "@/components/ui/typography";
import { authClient } from "@/lib/auth-client";
import { getErrorMessage } from "@/lib/form-errors";

const schema = z.object({
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
});

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") ?? "", [params]);
  const error = params.get("error");
  const [newPassword, setNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    newPassword?: string;
    general?: string;
  }>({});

  if (error) {
    return (
      <PublicGuard>
        <AuthShell
          title="Reset your password"
          description="This reset link is no longer valid. Request a new one to continue."
        >
          <div className="space-y-6">
            <Heading variant="section" as="h2" className="text-center">
              Reset link invalid or expired
            </Heading>
            <p className="text-center text-muted-foreground">
              Please request a new password reset link.
            </p>
            <Button
              onClick={() => navigate("/forgot-password")}
              className="w-full"
            >
              Request new link
            </Button>
          </div>
        </AuthShell>
      </PublicGuard>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      const message = "Missing reset token";
      setErrors({ general: message });
      toast.error(message);
      return;
    }
    setErrors({});
    const parsed = schema.safeParse({ newPassword });
    if (!parsed.success) {
      setErrors({
        newPassword:
          parsed.error.issues[0]?.message ??
          "Password must be at least 8 characters",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await authClient.resetPassword({ newPassword, token });
      setErrors({});
      toast.success("Password has been reset. Please sign in.");
      navigate("/sign-in");
    } catch (err: unknown) {
      const message = getErrorMessage(err, "Failed to reset password");
      setErrors({ general: message });
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicGuard>
      <AuthShell
        title="Set a new password"
        description="Choose a strong password with at least 8 characters."
      >
        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                aria-invalid={Boolean(errors.newPassword)}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword || errors.general) {
                    setErrors({});
                  }
                }}
                required
              />
              <FieldErrorMessage message={errors.newPassword} />
            </div>
            <FormErrorAlert message={errors.general} />
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? "Setting..." : "Set new password"}
            </Button>
          </form>
          <div className="text-center pt-4 border-t border-border">
            <Button
              variant="link"
              onClick={() => navigate("/sign-in")}
              className="font-medium text-foreground"
            >
              Back to login
            </Button>
          </div>
        </div>
      </AuthShell>
    </PublicGuard>
  );
}
